<script>
  // Chip showing how many framework items of one level a team covers.
  // Hovering lists them, grouped by their parent level.
  import { tick } from "svelte";
  import { Tag } from "paxray-components-svelte";

  export let label = "L1";
  export let title = "";
  export let breakdown = { count: 0, groups: [] };
  // Shows a red chip when nothing is assigned on this level.
  export let warnWhenEmpty = false;

  const COLUMN_WIDTH = 256;
  const GAP = 4;
  const MARGIN = 8;

  let open = false;
  let popover;
  let style = "";

  // Long lists spread over more columns instead of scrolling.
  $: columns = breakdown.count > 30 ? 3 : breakdown.count > 12 ? 2 : 1;

  // Fixed position so the table never clips it. Opens upwards when there is
  // more room above, and keeps it inside the viewport.
  async function show(event) {
    if (!breakdown.count) return;
    const rect = event.currentTarget.getBoundingClientRect();
    const width = Math.min(
      columns * COLUMN_WIDTH,
      window.innerWidth - 2 * MARGIN,
    );
    const widthStyle =
      columns === 1 ? `max-width: ${width}px;` : `width: ${width}px;`;
    const left = Math.max(
      MARGIN,
      Math.min(rect.left, window.innerWidth - width - MARGIN),
    );
    const below = window.innerHeight - rect.bottom - GAP - MARGIN;
    const above = rect.top - GAP - MARGIN;
    style = `left: ${left}px; ${widthStyle} top: ${rect.bottom + GAP}px; max-height: ${below}px;`;
    open = true;
    await tick();
    if (popover && popover.scrollHeight > below && above > below)
      style = `left: ${left}px; ${widthStyle} bottom: ${window.innerHeight - rect.top + GAP}px; max-height: ${above}px;`;
  }

</script>

<span
  class="level-chip"
  role="note"
  on:mouseenter={show}
  on:mouseleave={() => (open = false)}
>
  {#if warnWhenEmpty && !breakdown.count}
    <Tag type="red" title="Nothing assigned">{label}: <strong>0</strong></Tag>
  {:else}
    <Tag
      type="gray"
      style="background-color: var(--pax-smoke); color: var(--pax-black);"
      >{label}: <strong>{breakdown.count}</strong></Tag
    >
  {/if}
</span>

{#if open}
  <div
    class="level-popover"
    role="tooltip"
    bind:this={popover}
    {style}
  >
    <div class="level-popover-title">{title} ({breakdown.count})</div>
    <div class="level-groups" style="column-count: {columns};">
      {#each breakdown.groups as group}
        <div class="level-group">
          {#if group.name}<div class="level-group-name">{group.name}</div>{/if}
          <ul class:nested={group.name}>
            {#each group.items as item}<li>{item}</li>{/each}
          </ul>
        </div>
      {/each}
    </div>
  </div>
{/if}

<style>
  .level-chip {
    display: inline-flex;
    cursor: default;
  }

  .level-chip strong {
    font-weight: 600;
  }

  .level-popover {
    position: fixed;
    z-index: 1000;
    white-space: normal;
    overflow-y: auto;
    pointer-events: none;
    min-width: 10rem;
    padding: 0.6rem 0.75rem;
    background: var(--pax-black, #161616);
    color: var(--pax-white, #fff);
    border-radius: 4px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
    font-size: 0.8125rem;
    line-height: 1.35;
  }

  .level-popover-title {
    font-weight: 600;
    margin-bottom: 0.35rem;
  }

  .level-groups {
    column-gap: 1rem;
  }

  .level-group {
    break-inside: avoid;
    padding-bottom: 0.4rem;
  }

  .level-group-name {
    opacity: 0.7;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: block;
  }

  ul.nested {
    padding-left: 0.75rem;
  }
</style>
